import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useFY } from "../../context/FYContext";
import {
  LayoutDashboard, Atom, Laptop, Briefcase, Leaf, MapPin,
  Handshake, BookOpen, ChevronLeft, ChevronRight, ChevronDown, Users, Printer,
} from "lucide-react";

const PILLAR_NAV = [
  { path: "/stem",           label: "STEM Education",   icon: Atom,      color: "text-[#E2231A]" },
  { path: "/digital",        label: "Digital Literacy", icon: Laptop,    color: "text-[#1F2937]" },
  { path: "/employability",  label: "Employability",    icon: Briefcase, color: "text-[#6B7280]" },
  { path: "/sustainability", label: "Sustainability",   icon: Leaf,      color: "text-[#991B1B]" },
];

const NAV = [
  { path: "/",                 label: "Overview",         icon: LayoutDashboard },
  { path: "/geography",        label: "Geographic Reach", icon: MapPin },
  { path: "/beneficiaries",    label: "Beneficiaries",    icon: Users },
  { path: "/partners",         label: "Partners",         icon: Handshake },
  { path: "/stories",          label: "Impact Stories",   icon: BookOpen },
];

const titleOf = (path) => {
  const item = [...NAV, ...PILLAR_NAV].find((n) => n.path === path);
  return item?.label || "Lenovo CSR";
};

export default function YBFLayout({ children }) {
  const [collapsed, setCollapsed] = useState(false);
  const [fyOpen, setFyOpen] = useState(false);
  const { fy, setFy, list } = useFY();
  const loc = useLocation();
  const title = titleOf(loc.pathname);

  // 3 Pillars submenu — auto-expand when user is on a pillar page; otherwise collapsed by default
  const onPillarPage = PILLAR_NAV.some((p) => p.path === loc.pathname);
  const [pillarsOpen, setPillarsOpen] = useState(onPillarPage);

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50">
      {/* Sidebar */}
      <aside
        data-testid="ybf-sidebar"
        className={`flex flex-col bg-white border-r border-slate-200 transition-all duration-300 shadow-sm ${collapsed ? "w-14" : "w-52"}`}
        style={{ flexShrink: 0 }}
      >
        {/* Brand */}
        <div
          className={`flex items-center px-3 py-3 border-b border-slate-100 ${collapsed ? "justify-center" : ""}`}
          style={{ minHeight: 56 }}
        >
          {collapsed ? (
            <div className="w-8 h-8 rounded-md bg-[#E2231A] flex items-center justify-center flex-shrink-0">
              <span className="text-white text-sm font-extrabold leading-none">L</span>
            </div>
          ) : (
            <img
              src="/lenovo-logo.svg"
              alt="Lenovo"
              className="h-9 w-auto"
              data-testid="ybf-brand-logo"
            />
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 py-3 px-1.5 overflow-y-auto">
          {NAV.map((item, idx) => {
            const link = (
              <NavLink
                key={item.path}
                to={item.path}
                data-testid={`ybf-nav-${item.path.replace("/", "") || "home"}`}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-2 py-2 rounded-md mb-0.5 transition-all duration-150 ${
                    isActive
                      ? "bg-[#1A1A1A]/5 text-[#1A1A1A] font-semibold border-l-2 border-[#E2231A]"
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  } ${collapsed ? "justify-center" : ""}`
                }
                end={item.path === "/"}
              >
                <item.icon size={15} strokeWidth={1.75} className={`flex-shrink-0 ${item.color || ""}`} />
                {!collapsed && <span className="text-[12px] truncate">{item.label}</span>}
              </NavLink>
            );

            // Inject the collapsible "3Es" submenu right after Overview (idx === 0)
            if (idx === 0) {
              return (
                <div key="overview-with-pillars">
                  {link}
                  {/* Collapsible Pillars section */}
                  {!collapsed ? (
                    <div data-testid="ybf-pillars-group">
                      <button
                        type="button"
                        onClick={() => setPillarsOpen((o) => !o)}
                        data-testid="ybf-pillars-toggle"
                        aria-expanded={pillarsOpen}
                        className={`flex items-center justify-between w-full px-2 py-2 rounded-md mb-0.5 transition-colors ${
                          onPillarPage
                            ? "text-[#1A1A1A] font-semibold"
                            : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <span className="w-[15px] h-[15px] inline-flex items-center justify-center text-[9px] font-bold rounded bg-gradient-to-br from-blue-500 via-violet-500 to-emerald-500 text-white flex-shrink-0">
                            4
                          </span>
                          <span className="text-[12px]">Focus Areas</span>
                        </span>
                        <ChevronRight
                          size={12}
                          className={`text-slate-400 transition-transform ${pillarsOpen ? "rotate-90" : ""}`}
                        />
                      </button>
                      {pillarsOpen && (
                        <div className="ml-3 border-l border-slate-200 pl-1.5 mb-1">
                          {PILLAR_NAV.map((p) => (
                            <NavLink
                              key={p.path}
                              to={p.path}
                              data-testid={`ybf-nav-${p.path.replace("/", "")}`}
                              className={({ isActive }) =>
                                `flex items-center gap-2 px-2 py-1.5 rounded-md mb-0.5 transition-all duration-150 ${
                                  isActive
                                    ? "bg-[#1A1A1A]/5 text-[#1A1A1A] font-semibold border-l-2 border-[#E2231A]"
                                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                                }`
                              }
                            >
                              <p.icon size={13} strokeWidth={1.75} className={`flex-shrink-0 ${p.color || ""}`} />
                              <span className="text-[11.5px] truncate">{p.label}</span>
                            </NavLink>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    /* Collapsed sidebar: render pillar icons stacked directly */
                    PILLAR_NAV.map((p) => (
                      <NavLink
                        key={p.path}
                        to={p.path}
                        data-testid={`ybf-nav-${p.path.replace("/", "")}`}
                        className={({ isActive }) =>
                          `flex items-center justify-center px-2 py-2 rounded-md mb-0.5 transition-all duration-150 ${
                            isActive
                              ? "bg-[#1A1A1A]/5 text-[#1A1A1A] border-l-2 border-[#E2231A]"
                              : "text-slate-600 hover:bg-slate-50"
                          }`
                        }
                        title={p.label}
                      >
                        <p.icon size={15} strokeWidth={1.75} className={`flex-shrink-0 ${p.color || ""}`} />
                      </NavLink>
                    ))
                  )}
                </div>
              );
            }
            return link;
          })}
        </nav>

        {/* Disclaimer */}
        {!collapsed && (
          <div
            data-testid="ybf-sidebar-disclaimer"
            className="border-t border-slate-100 px-3 py-2.5 text-[11px] leading-snug text-slate-500 bg-slate-50/60"
          >
            <p>
              This portal is developed by{" "}
              <span className="font-semibold text-slate-700">Sattva</span> &amp;{" "}
              <span className="font-semibold text-slate-700">Dhwani RIS</span>{" "}
              for demo purposes. Data is mock; some representative names &amp; context
              are taken from public-domain CSR material. Logos and brand assets
              belong to <span className="font-semibold text-slate-700">"Lenovo"</span>{" "}
              and are used for demo only. Any other use of this portal is not allowed.
            </p>
          </div>
        )}

        {/* Collapse */}
        <div className="border-t border-slate-100 p-2">
          <button
            onClick={() => setCollapsed((c) => !c)}
            data-testid="ybf-sidebar-collapse"
            className={`flex items-center gap-1.5 w-full px-2 py-1 rounded-md text-[10px] text-slate-500 hover:bg-slate-50 transition-colors ${collapsed ? "justify-center" : ""}`}
          >
            {collapsed ? <ChevronRight size={12} /> : <><ChevronLeft size={12} /><span>Collapse</span></>}
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        {/* Header */}
        <header
          data-testid="ybf-header"
          className="bg-[#1A1A1A] text-white px-5 flex items-center justify-between shadow-sm"
          style={{ height: 48, flexShrink: 0 }}
        >
          <div className="flex items-center gap-3">
            <span className="text-white text-[13px] font-bold tracking-wide">Lenovo</span>
            <span className="w-px h-4 bg-white/20" />
            <span className="text-white/70 text-[11px]">CSR Impact Dashboard</span>
            <span className="w-px h-4 bg-white/20" />
            <span className="text-white text-[12px] font-medium">{title}</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => {
                document.title = `Lenovo CSR · ${title} · ${fy}`;
                window.print();
              }}
              data-testid="print-pdf-button"
              title="Print / Save as PDF"
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white/10 hover:bg-white/15 text-white text-[11px] font-semibold transition-colors print:hidden"
            >
              <Printer size={12} />
              <span className="hidden sm:inline">Print</span>
            </button>
            <div className="relative" data-testid="fy-switcher">
              <button
                onClick={() => setFyOpen((o) => !o)}
                onBlur={() => setTimeout(() => setFyOpen(false), 150)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white/10 hover:bg-white/15 text-white text-[11px] font-semibold transition-colors"
                data-testid="fy-switcher-button"
              >
                <span className="text-white/60 font-medium text-[10px] uppercase tracking-wider">FY</span>
                <span>{fy.replace("FY ", "")}</span>
                <ChevronDown size={11} className={`transition-transform ${fyOpen ? "rotate-180" : ""}`} />
              </button>
              {fyOpen && (
                <div
                  className="absolute right-0 top-full mt-1 bg-white text-slate-900 rounded-lg shadow-lg border border-slate-200 overflow-hidden z-50 min-w-[120px]"
                  data-testid="fy-switcher-menu"
                >
                  {list.slice().reverse().map((f) => (
                    <button
                      key={f}
                      onMouseDown={(e) => { e.preventDefault(); setFy(f); setFyOpen(false); }}
                      data-testid={`fy-option-${f.replace(/\s+/g, "-")}`}
                      className={`block w-full text-left px-3 py-1.5 text-[11px] font-medium hover:bg-slate-50 ${
                        fy === f ? "bg-[#1A1A1A]/5 text-[#1A1A1A] font-bold" : "text-slate-700"
                      }`}
                    >
                      {f}
                      {fy === f && <span className="ml-2 text-[9px] text-[#E2231A] font-bold">● CURRENT</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#E2231A]/90 text-white text-[9px] font-semibold uppercase tracking-wider">
              Mock
            </span>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto px-5 py-4">{children}</main>
      </div>
    </div>
  );
}
